import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useUserProfile } from "../../auth/UserProfileContext";
import { supabase, dbToDeal, fetchAllRows } from "../../supabase";
import {
  loadRecentActivity,
  loadTasks,
  createTask,
  updateTask,
  deleteTask,
  loadTeamRoster,
  logActivity,
} from "../../services/activityLogger";
import { callClaudeWithMessages } from "../../services/claude";
import { getCurrentWeekWindow, getWeekStartISO, getWeekEndISO } from "../../utils/weeklyWindow";
import PPLogo from "../common/PPLogo";
import QuarterlySummaryModal from "./QuarterlySummaryModal";
import { BRIEF as C } from "../../constants/briefTheme";
import {
  activeBook, equityLadder, marketsOnce, mapRegions, returnsSummary,
  daysInStage, watchlist, weekMoves, partnerSummary as buildPartnerSummary, DEFAULT_EQUITY_PCT,
} from "../../services/homeView";
import { pipelineFunnel, deepestStageIndex, killMatrix } from "../../services/pipelineAnalytics";
import { collapseSlips } from "../../services/stageLog";
import useHoverTip from "./useHoverTip";
import HomeUpdate from "./HomeUpdate";
import BookHero from "./BookHero";
import EquityByStage from "./EquityByStage";
import CloseCards from "./CloseCards";
import WhereMaps from "./WhereMaps";
import MarketsOnce from "./MarketsOnce";
import ReturnsStrip from "./ReturnsStrip";
import HomeFunnel from "./HomeFunnel";
import BookTable from "./BookTable";
import Watchlist from "./Watchlist";
import WeekMoves from "./WeekMoves";

// ═══════════════════════════════════════════════════════════════════════════════
// HOME DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
// Three sections:
//   1. Recent Updates — live activity feed
//   2. Tasks — assigned goals with auto-progress
//   3. Tuesday Meeting — "Present Weekly Meeting" button → full-screen deck

const ACTION_LABELS = {
  loi_sent: "LOI Sent",
  deal_created: "Deal Created",
  stage_changed: "Stage Changed",
  comp_added: "Comp Added",
  property_added: "Property Added",
  property_deleted: "Property Deleted",
  contact_added: "Contact Added",
  document_uploaded: "Document Uploaded",
  outreach_status: "Outreach Updated",
  deal_imported: "Deal Imported",
  email_summarized: "Email Summarized",
  task_completed: "Task Completed",
};

const ACTION_COLORS = {
  loi_sent: "#e93529",
  deal_created: "#4f9d76",
  stage_changed: "#b4b4b4",
  comp_added: "#8a8a8a",
  property_added: "#4f9d76",
  property_deleted: "#e93529",
  contact_added: "#8a8a8a",
  document_uploaded: "#c8922f",
  outreach_status: "#c8922f",
  deal_imported: "#4f9d76",
  email_summarized: "#8a8a8a",
  task_completed: "#44906a",
};

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function HomeDashboard({ onNavigate, onPresent, presentationSaveTick = 0 }) {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { profile } = useUserProfile();
  const currentEmail = profile?.email || account?.username || "";
  const currentName = profile?.name || account?.name || "";

  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamRoster, setTeamRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  // Task creation modal
  const [showNewTask, setShowNewTask] = useState(false);
  const [quarterlyModalOpen, setQuarterlyModalOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", assigned_to: "", timeframe: "week", target_count: "", due_date: "" });
  // Task edit modal — null when closed, otherwise holds the in-progress edit form.
  const [editingTask, setEditingTask] = useState(null);

  // Pipeline stats for the meeting section
  const [pipelineStats, setPipelineStats] = useState(null);
  // Equity share assumed against book value. Lives in app_settings so the
  // assumption stays editable. Home shows no fund figures, so there is no
  // target beside it.
  const [equityPct, setEquityPct] = useState(DEFAULT_EQUITY_PCT);
  // Present: the same page with the team's working sections, deal aging and
  // data notes taken out, for partners and investors in the room.
  const [present, setPresent] = useState(false);
  const [tipEl, bindTip] = useHoverTip();
  // Presentation history
  const [presentationHistory, setPresentationHistory] = useState([]);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  // Items to Discuss (raw notes → AI polishes for presentation)
  const [agendaNotes, setAgendaNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [polishingNotes, setPolishingNotes] = useState(false);

  const refresh = useCallback(async () => {
    // Weekly window: Wednesday 12am EST → Tuesday 11:59pm EST
    const { weekStart, weekEnd } = getCurrentWeekWindow();
    const wsISO = weekStart.toISOString();
    const weISO = weekEnd.toISOString();

    // Weekly PDF history, ordered by the week each snapshot COVERS. Ordering
    // by presented_at put weeks out of sequence whenever one was presented
    // late — 5/26 landed above 5/27 because it was clicked later the same
    // evening.
    const { data: presHistory } = await supabase
      .from("presentation_history").select("*")
      .order("week_start", { ascending: false, nullsFirst: false })
      .order("presented_at", { ascending: false })
      .limit(20);
    setPresentationHistory(presHistory || []);

    // Agenda notes — scoped to the current week only
    const { data: notes } = await supabase.from("agenda_notes").select("*").gte("created_at", wsISO).lte("created_at", weISO).order("created_at", { ascending: true });
    setAgendaNotes(notes || []);

    // Activity — scoped to the current week. Feeds the 72-hour feed and the
    // week's stage moves.
    const { data: weekActs } = await supabase.from("activity_log").select("*").gte("created_at", wsISO).lte("created_at", weISO).order("created_at", { ascending: false }).limit(500);
    setActivities(weekActs || []);

    const { data: settings } = await supabase.from("app_settings").select("key, value").eq("key", "equity_pct");
    for (const st of settings || []) {
      const n = Number(st.value);
      if (Number.isFinite(n) && n > 0 && n <= 1) setEquityPct(n);
    }

    // Tasks — show active ones (including rollover from past weeks) + completed this week
    const { data: allTasks } = await supabase.from("tasks").select("*").order("created_at", { ascending: false });
    const visibleTasks = (allTasks || []).filter(t =>
      t.status === "active" || // always show active (includes rollovers)
      (t.status === "completed" && t.updated_at >= wsISO && t.updated_at <= weISO) // completed this week
    );
    setTasks(visibleTasks);

    const roster = await loadTeamRoster();
    setTeamRoster(roster);

    // Stage-history log, all-time. The funnel reads the deepest stage each deal
    // reached from it; the watchlist reads when each deal entered its stage.
    const { data: stageLog } = await fetchAllRows(() => supabase
      .from("activity_log")
      .select("action, entity_id, metadata, created_at")
      .in("action", ["stage_changed", "deal_created"])
      .order("created_at", { ascending: true })
      .order("id"));

    const PAGE = 1000;
    let allDeals = [];
    let from = 0;
    while (true) {
      const { data } = await supabase.from("deals").select("*").eq("deal_flow", "pipeline").range(from, from + PAGE - 1);
      if (!data || data.length === 0) break;
      allDeals = allDeals.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }

    setPipelineStats({ deals: allDeals.map(dbToDeal), stageLog: collapseSlips(stageLog || []) });
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // The deck writes the weekly snapshot, then bumps this counter through App
  // so the history dropdown picks up the new week without the deck having to
  // reach into this component.
  useEffect(() => {
    if (presentationSaveTick > 0) refresh();
  }, [presentationSaveTick, refresh]);

  // Auto-refresh every 30 seconds — except while presenting. A number that
  // moves in front of the room is worse than one that is thirty seconds old;
  // the live book once dropped from 17 deals to 12 inside two hours.
  useEffect(() => {
    if (present) return undefined;
    const interval = setInterval(() => refresh(), 30000);
    return () => clearInterval(interval);
  }, [refresh, present]);

  // ── Task handlers ──────────────────────────────────────────────────────────
  async function handleCreateTask() {
    if (!newTask.title || !newTask.assigned_to) return;
    const roster = teamRoster.find(u => u.email === newTask.assigned_to);
    await createTask({
      title: newTask.title,
      assigned_to: newTask.assigned_to,
      assigned_to_name: roster?.display_name || newTask.assigned_to,
      assigned_by: currentEmail,
      assigned_by_name: currentName,
      timeframe: newTask.timeframe,
      target_count: newTask.target_count ? Number(newTask.target_count) : null,
      due_date: newTask.due_date || null,
    });
    setNewTask({ title: "", assigned_to: "", timeframe: "week", target_count: "", due_date: "" });
    setShowNewTask(false);
    refresh();
  }

  async function handleCompleteTask(task) {
    await updateTask(task.id, { status: "completed", current_count: task.target_count || task.current_count });
    await logActivity({
      userName: currentName,
      userEmail: currentEmail,
      action: "task_completed",
      description: `Completed: ${task.title}${task.target_count ? ` (${task.target_count}/${task.target_count})` : ""}`,
      entityType: "task",
      entityId: task.id,
      metadata: { title: task.title, assigned_to: task.assigned_to_name },
    });
    refresh();
  }

  function startEditTask(task) {
    setEditingTask({
      id: task.id,
      title: task.title || "",
      assigned_to: task.assigned_to || "",
      timeframe: task.timeframe || "week",
      target_count: task.target_count != null ? String(task.target_count) : "",
      current_count: task.current_count != null ? String(task.current_count) : "",
      due_date: task.due_date ? String(task.due_date).slice(0, 10) : "",
      status: task.status || "active",
    });
  }

  async function handleSaveEditTask() {
    if (!editingTask || !editingTask.title || !editingTask.assigned_to) return;
    const roster = teamRoster.find(u => u.email === editingTask.assigned_to);
    await updateTask(editingTask.id, {
      title: editingTask.title,
      assigned_to: editingTask.assigned_to,
      assigned_to_name: roster?.display_name || editingTask.assigned_to,
      timeframe: editingTask.timeframe,
      target_count: editingTask.target_count ? Number(editingTask.target_count) : null,
      current_count: editingTask.current_count ? Number(editingTask.current_count) : 0,
      due_date: editingTask.due_date || null,
      status: editingTask.status,
    });
    setEditingTask(null);
    refresh();
  }

  async function handleDeleteTask(task) {
    if (!window.confirm(`Delete task "${task.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await deleteTask(task.id);
      refresh();
    } catch (e) {
      window.alert(`Couldn't delete task: ${e.message || e}`);
    }
  }

  // ── Present the weekly meeting ─────────────────────────────────────────────
  // Freezes the current week into presentation_history (upserting so a second
  // press the same week updates rather than duplicating), then hands off to
  // the full-screen deck. Lifted out of the old Weekly Review block's inline
  // handler so the Partner Brief hero can trigger the same flow.
  // The deck owns the weekly snapshot now: it loads the data the slides
  // render, so it's the only place that can store a payload guaranteed to
  // match the PDF. This just opens it in export mode.
  function handleExportWeekly() {
    onPresent?.({ mode: "export" });
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="flex gap-1.5">
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    );
  }

  // ── What the page shows ─────────────────────────────────────────────────────
  // Everything below the top bar derives from services/homeView and from the
  // funnel Pipeline draws, off the data `refresh` already loaded.
  const deals = pipelineStats?.deals || [];
  const stageLog = pipelineStats?.stageLog || [];
  const book = activeBook(deals);
  const ladder = equityLadder(book, equityPct);
  const markets = marketsOnce(book);
  const regions = mapRegions(book);
  const returns = returnsSummary(book);
  const ages = daysInStage(deals, stageLog);
  const watch = watchlist(book, ages);
  const moves = weekMoves(activities);
  const funnel = pipelineFunnel(deals, deepestStageIndex(stageLog));
  const reasons = (killMatrix(deals).ranked || []).slice(0, 3);   // ranked is absent when nothing carries a reason
  const closeRows = book.rows.filter((r) => r.stage !== "Underwriting");
  const partnerSummary = buildPartnerSummary(book, returns);
  const openDeal = (deal) => onNavigate?.("pipeline", { focusDealId: deal.id });
  const weekLabel = getCurrentWeekWindow().weekLabel;
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  // Export PDF prints the Present view, so the paper always carries the
  // masthead and the disclaimer and never the team's working sections.
  function exportPresentPdf() {
    const wasPresent = present;
    setPresent(true);
    setTimeout(() => { window.print(); if (!wasPresent) setPresent(false); }, 400);
  }

  // Activity feed, trimmed to 72 hours. On a page checked daily, anything
  // older is a museum piece — the full history stays in the weekly deck.
  const recentActivities = (() => {
    const cutoff = Date.now() - 3 * 86_400_000;
    return activities.filter(a => {
      const t = new Date(a.created_at).getTime();
      return Number.isFinite(t) && t >= cutoff;
    });
  })();

  // Group tasks by person
  const tasksByPerson = {};
  tasks.filter(t => t.status === "active" || t.status === "completed").forEach(t => {
    const key = t.assigned_to_name || t.assigned_to;
    if (!tasksByPerson[key]) tasksByPerson[key] = [];
    tasksByPerson[key].push(t);
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 font-montserrat">
      {/* Edit-task modal overlay */}
      {editingTask && (
        <div
          onClick={() => setEditingTask(null)}
          style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-2xl"
            style={{ width: "100%", maxWidth: 480, background: "#242424", border: "1px solid #3a3a3a", padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold" style={{ color: "#f4f4f4" }}>Edit Task</h3>
              <button onClick={() => setEditingTask(null)} style={{ color: "#8a8a8a", fontSize: 18, lineHeight: 1, background: "none", border: "none", cursor: "pointer", padding: 4 }}>✕</button>
            </div>
            <input
              value={editingTask.title}
              onChange={e => setEditingTask(p => ({ ...p, title: e.target.value }))}
              placeholder="Task title"
              className="w-full rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-pp-red/40"
              style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
            />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <select
                value={editingTask.assigned_to}
                onChange={e => setEditingTask(p => ({ ...p, assigned_to: e.target.value }))}
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              >
                <option value="">Assign to...</option>
                {teamRoster.map(u => (
                  <option key={u.email} value={u.email}>{u.display_name || u.email}</option>
                ))}
              </select>
              <select
                value={editingTask.timeframe}
                onChange={e => setEditingTask(p => ({ ...p, timeframe: e.target.value }))}
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <input
                type="number" min="0"
                value={editingTask.target_count}
                onChange={e => setEditingTask(p => ({ ...p, target_count: e.target.value }))}
                placeholder="Target"
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              />
              <input
                type="number" min="0"
                value={editingTask.current_count}
                onChange={e => setEditingTask(p => ({ ...p, current_count: e.target.value }))}
                placeholder="Progress"
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              />
              <input
                type="date"
                value={editingTask.due_date}
                onChange={e => setEditingTask(p => ({ ...p, due_date: e.target.value }))}
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              />
            </div>
            <div className="mb-3">
              <select
                value={editingTask.status}
                onChange={e => setEditingTask(p => ({ ...p, status: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={async () => {
                  const t = editingTask;
                  setEditingTask(null);
                  await handleDeleteTask({ id: t.id, title: t.title });
                }}
                className="text-xs font-semibold px-3 py-2 rounded-lg transition-colors hover:bg-[rgba(233,53,41,0.07)]"
                style={{ color: "#e93529", border: "1px solid rgba(233,53,41,0.3)" }}
              >
                Delete
              </button>
              <div className="flex gap-2">
                <button onClick={() => setEditingTask(null)} className="text-xs px-3 py-2 rounded-lg bg-[#ebebeb] hover:bg-[#d8d8d8]" style={{ color: "#f4f4f4" }}>
                  Cancel
                </button>
                <button
                  onClick={handleSaveEditTask}
                  disabled={!editingTask.title || !editingTask.assigned_to}
                  className="bg-pp-red hover:bg-pp-red-light disabled:opacity-40 text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                  style={{ color: "#fff" }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tipEl}

      {/* Top bar. Present is this same page with the team's working sections,
          aging and data notes taken out, and auto-refresh paused. */}
      <div data-print-hide className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          {!present && (
            <>
              <h1 className="text-2xl font-semibold text-[#f4f4f4]" style={{ letterSpacing: "-0.02em" }}>Acquisitions</h1>
              <p className="text-[12.5px] mt-1 text-[#8a8a8a]">{today} · Live</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex overflow-hidden rounded-lg border border-[#3a3a3a] bg-[#242424]">
            {[["Working", false], ["Present", true]].map(([label, val]) => (
              <button key={label} onClick={() => setPresent(val)}
                className={`px-3.5 py-2 text-[12px] font-semibold ${present === val ? "bg-[#e93529]" : "text-[#b4b4b4] hover:text-[#f4f4f4]"}`}
                style={present === val ? { color: "#ffffff" } : {}}>
                {label}
              </button>
            ))}
          </div>
          {!present && (
            <>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(partnerSummary)
                    .then(() => alert("Partner summary copied to clipboard."))
                    .catch(() => {});
                }}
                className="h-9 px-3.5 rounded-md text-[12px] font-semibold transition-colors border border-[#3a3a3a] bg-[#242424] text-[#b4b4b4] hover:text-[#f4f4f4] hover:bg-[#2c2c2c]">
                Copy summary
              </button>
              {presentationHistory.length > 0 && (
                <select
                  value={selectedPresentation?.id || ""}
                  onChange={(e) => setSelectedPresentation(presentationHistory.find(p => p.id === e.target.value) || null)}
                  className="h-9 px-3 rounded-md text-[12px] font-medium border border-[#3a3a3a] bg-[#242424] text-[#b4b4b4] focus:outline-none focus:ring-2 focus:ring-pp-red/30">
                  <option value="">Past weekly PDFs</option>
                  {presentationHistory.map(p => (
                    <option key={p.id} value={p.id}>{p.week_label}</option>
                  ))}
                </select>
              )}
              {selectedPresentation && (
                <button onClick={() => onPresent?.({ mode: "export", snapshot: selectedPresentation.snapshot })} className="h-9 px-3.5 rounded-md text-[12px] font-semibold transition-colors border border-[#3a3a3a] bg-[#242424] text-[#b4b4b4] hover:text-[#f4f4f4] hover:bg-[#2c2c2c]">
                  Rebuild PDF
                </button>
              )}
              <button onClick={handleExportWeekly} className="h-9 px-3.5 rounded-md text-[12px] font-semibold transition-colors border border-[#3a3a3a] bg-[#242424] text-[#b4b4b4] hover:text-[#f4f4f4] hover:bg-[#2c2c2c]"
                title="Saves this week's snapshot and exports the weekly deck as a PDF">
                Weekly PDF
              </button>
            </>
          )}
          <button onClick={exportPresentPdf}
            className="h-9 px-3.5 rounded-md text-[12px] font-semibold bg-[#e93529] hover:bg-[#f04f44] transition-colors"
            style={{ color: "#ffffff" }}
            title="Prints this page in Present mode">
            Export PDF
          </button>
        </div>
      </div>

      {present && (
        <div className="mb-6 flex items-end justify-between border-b-2 border-[#e93529] pb-3.5">
          <div>
            <div className="text-[10.5px] font-bold tracking-[0.2em] text-[#e93529]">PROXIMITYPARKS USA · FOUNDERS FUND</div>
            <div className="mt-1 text-[26px] font-semibold text-[#f4f4f4]" style={{ letterSpacing: "-0.02em" }}>Acquisitions Update</div>
          </div>
          <div className="text-right text-[11.5px] leading-relaxed text-[#8a8a8a]">As of {today}<br />{weekLabel}</div>
        </div>
      )}

      {pipelineStats && (
        <>
          <HomeUpdate present={present} authorName={currentName} />

          <div className="grid gap-4 mb-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <BookHero book={book} bind={bindTip} />
            <EquityByStage ladder={ladder} equityPct={equityPct} bind={bindTip} />
          </div>

          <div className="mt-7 mb-3 px-0.5">
            <h3 className="text-[15px] font-semibold text-[#f4f4f4]">Closest to close</h3>
            <p className="mt-0.5 text-[12px] text-[#8a8a8a]">In diligence and under LOI</p>
          </div>
          <div className="mb-7">
            <CloseCards rows={closeRows} ages={ages} present={present} equityPct={equityPct} onOpenDeal={openDeal} />
          </div>

          <div className="grid gap-4 mb-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <WhereMaps regions={regions} />
            <MarketsOnce markets={markets} bind={bindTip} />
          </div>

          <div className="grid gap-4 mb-4 xl:grid-cols-2">
            <ReturnsStrip returns={returns} bind={bindTip} />
            <HomeFunnel funnel={funnel} reasons={reasons} />
          </div>

          <div className="mb-4">
            <BookTable book={book} onOpenDeal={openDeal} />
          </div>

          {present && (
            <p className="mt-7 border-t border-[#3a3a3a] pt-3.5 text-[10.5px] leading-relaxed text-[#6f6f6f]">
              Figures reflect ProximityParks' internal underwriting as of {today} and are estimates, not forecasts of realized returns.
              Book value uses our underwritten price where one exists and the seller's asking price otherwise. Equity assumes {Math.round(equityPct * 100)}% of book value.
              Deals under review may not close. Confidential — prepared for ProximityParks USA Founders Fund investors.
            </p>
          )}
        </>
      )}

      {/* ═══ Team only — hidden when presenting and when printed ═══ */}
      {!present && (
      <div data-print-hide>
      <div className="my-8 flex items-center gap-3.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#8a8a8a]">
        <span className="flex-1 border-t border-[#3a3a3a]" />
        Team only · hidden when presenting
        <span className="flex-1 border-t border-[#3a3a3a]" />
      </div>

      <div className="grid gap-4 mb-6 xl:grid-cols-2">
        <Watchlist watch={watch} onOpenDeal={openDeal} />
        <WeekMoves moves={moves} weekLabel={weekLabel} />
      </div>

      {/* Tasks now take the 2/3 slot and the activity feed takes 1/3 — what
          you need to DO outranks what already happened. The feed is also
          trimmed to the last 72 hours; on a page you open daily, older
          events are noise. */}
      <div data-print-hide className="grid grid-cols-3 gap-6 mb-6">
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* SECTION 2: ACTIVITY FEED (right 1 col, 72h window) */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="order-2">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base font-bold" style={{ color: C.ink }}>Activity</h2>
            <span className="text-xs" style={{ color: C.faint }}>last 72 hours</span>
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}`, backgroundColor: C.card, maxHeight: 600, overflowY: "auto" }}>
            {recentActivities.length === 0 ? (
              <div className="p-8 text-center" style={{ color: C.faint }}>
                <p className="text-sm">Nothing in the last three days.</p>
              </div>
            ) : (
              <div>
                {recentActivities.map((a, i) => (
                  <div key={a.id || i} className="flex items-start gap-2.5 px-4 py-2.5 transition-colors hover:bg-[#f4f4f4]" style={{ borderBottom: i < recentActivities.length - 1 ? `1px solid ${C.divider}` : "none" }}>
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: ACTION_COLORS[a.action] || C.faint }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12.5px] leading-snug" style={{ color: C.ink2 }}>
                        <span className="font-medium">{a.description}</span>
                      </p>
                      <p className="text-[10.5px] mt-0.5" style={{ color: C.faint }}>
                        {a.user_name} &middot; {formatTimeAgo(a.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* SECTION 3: TASKS (left 2 cols — promoted above the activity feed)  */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="col-span-2 order-1">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base font-bold" style={{ color: C.ink }}>Assigned to the team</h2>
            <button onClick={() => setShowNewTask(true)} className="text-xs font-semibold transition-colors hover:opacity-70" style={{ color: C.accent }}>+ Assign task</button>
          </div>

          {/* New task form */}
          {showNewTask && (
            <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: "#242424", border: "1px solid #3a3a3a" }}>
              <input
                value={newTask.title}
                onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))}
                placeholder="Task title (e.g. Send 50 LOIs this week)"
                className="w-full rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-pp-red/40"
                style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
              />
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  value={newTask.assigned_to}
                  onChange={e => setNewTask(p => ({ ...p, assigned_to: e.target.value }))}
                  className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
                >
                  <option value="">Assign to...</option>
                  {teamRoster.map(u => (
                    <option key={u.email} value={u.email}>{u.display_name || u.email}</option>
                  ))}
                </select>
                <select
                  value={newTask.timeframe}
                  onChange={e => setNewTask(p => ({ ...p, timeframe: e.target.value }))}
                  className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input
                  type="number"
                  value={newTask.target_count}
                  onChange={e => setNewTask(p => ({ ...p, target_count: e.target.value }))}
                  placeholder="Target (e.g. 50)"
                  className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
                />
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={e => setNewTask(p => ({ ...p, due_date: e.target.value }))}
                  className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ border: "1px solid #3a3a3a", color: "#f4f4f4" }}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleCreateTask} disabled={!newTask.title || !newTask.assigned_to}
                  className="bg-pp-red hover:bg-pp-red-light disabled:opacity-40 text-xs font-semibold px-4 py-2 rounded-lg transition-colors" style={{ color: "#fff" }}>
                  Assign
                </button>
                <button onClick={() => setShowNewTask(false)} className="text-xs px-3 py-2 rounded-lg bg-[#ebebeb] hover:bg-[#d8d8d8]" style={{ color: "#f4f4f4" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Tasks by person */}
          <div className="space-y-4" style={{ maxHeight: 600, overflowY: "auto" }}>
            {Object.keys(tasksByPerson).length === 0 ? (
              <div className="rounded-xl p-6 text-center" style={{ border: "1px solid #3a3a3a" }}>
                <p className="text-sm" style={{ color: "#8a8a8a" }}>No active tasks. Click "+ Assign Task" to get started.</p>
              </div>
            ) : (
              Object.entries(tasksByPerson).map(([person, personTasks]) => (
                <div key={person} className="rounded-xl overflow-hidden" style={{ border: "1px solid #3a3a3a" }}>
                  <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: "#2c2c2c", borderBottom: "1px solid #3a3a3a" }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: "#e93529", color: "#fff" }}>
                      {(person[0] || "?").toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold" style={{ color: "#f4f4f4" }}>{person}</span>
                    <span className="text-[10px] ml-auto" style={{ color: "#8a8a8a" }}>{personTasks.length} active</span>
                  </div>
                  <div className="divide-y" style={{ borderColor: "#f4f4f4" }}>
                    {personTasks.map(task => {
                      const progress = task.target_count ? Math.min(100, Math.round((task.current_count / task.target_count) * 100)) : null;
                      return (
                        <div key={task.id} className="px-4 py-3" style={task.status === "completed" ? { backgroundColor: "rgba(68,144,106,0.06)", borderLeft: "3px solid #44906a" } : {}}>
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-medium" style={{ color: "#f4f4f4" }}>
                              {task.status === "completed" && <span style={{ color: "#44906a", marginRight: 4 }}>✓</span>}
                              {task.title}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {task.status === "active" ? (
                                <button
                                  onClick={() => handleCompleteTask(task)}
                                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors hover:bg-[rgba(79,157,118,0.16)]"
                                  style={{ color: "#44906a" }}
                                >
                                  Complete
                                </button>
                              ) : (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(68,144,106,0.12)", color: "#44906a" }}>
                                  Done
                                </span>
                              )}
                              <button
                                onClick={() => startEditTask(task)}
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-colors hover:bg-[#ebebeb]"
                                style={{ color: "#6a6a6a" }}
                                title="Edit task"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task)}
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-colors hover:bg-[rgba(233,53,41,0.07)]"
                                style={{ color: "#e93529" }}
                                title="Delete task"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-[10px]" style={{ color: "#8a8a8a" }}>
                            <span className="uppercase font-semibold tracking-wider" style={{ color: task.timeframe === "day" ? "#e93529" : task.timeframe === "week" ? "#8a8a8a" : "#b4b4b4" }}>
                              {task.timeframe}
                            </span>
                            {task.due_date && <span>Due {new Date(task.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                            {task.assigned_by_name && <span>by {task.assigned_by_name}</span>}
                          </div>
                          {progress !== null && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] font-semibold" style={{ color: "#f4f4f4" }}>{task.current_count} / {task.target_count}</span>
                                <span className="text-[10px] font-bold" style={{ color: progress >= 100 ? "#44906a" : "#e93529" }}>{progress}%</span>
                              </div>
                              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "#e4e4e4" }}>
                                <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: progress >= 100 ? "#44906a" : "#e93529" }} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ITEMS TO DISCUSS — raw notes that get polished into agenda items */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="mb-6 rounded-2xl overflow-hidden" style={{ backgroundColor: "#242424", border: "1px solid #3a3a3a" }}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #3a3a3a" }}>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="#e93529" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            <h2 className="text-sm font-bold" style={{ color: "#f4f4f4" }}>For the partners</h2>
            <span className="text-[10px]" style={{ color: "#8a8a8a" }}>Decisions and agenda items — Proxy polishes them for the meeting</span>
          </div>
          {agendaNotes.length > 0 && (
            <button
              onClick={async () => {
                setPolishingNotes(true);
                try {
                  const rawNotes = agendaNotes.map(n => n.text).join("\n- ");
                  const result = await callClaudeWithMessages(
                    `You are a CRE acquisitions team assistant. Take these raw meeting notes and turn each into a concise, professional agenda item (1-2 sentences max). Return ONLY a JSON array of strings, no prose.`,
                    [{ role: "user", content: `Raw notes:\n- ${rawNotes}` }],
                    1024
                  );
                  const cleaned = result.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
                  const polished = JSON.parse(cleaned);
                  // Update each note with its polished version
                  for (let i = 0; i < Math.min(polished.length, agendaNotes.length); i++) {
                    await supabase.from("agenda_notes").update({ polished: polished[i] }).eq("id", agendaNotes[i].id);
                  }
                  refresh();
                } catch (e) { console.error("Polish failed:", e); }
                finally { setPolishingNotes(false); }
              }}
              disabled={polishingNotes}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
              style={{ color: "#fff", backgroundColor: "#e93529", opacity: polishingNotes ? 0.5 : 1 }}
            >
              {polishingNotes ? "Polishing..." : "Polish for Meeting"}
            </button>
          )}
        </div>
        <div className="px-5 py-3">
          {/* Existing notes */}
          {agendaNotes.length > 0 && (
            <div className="space-y-2 mb-3">
              {agendaNotes.map(note => (
                <div key={note.id} className="flex items-start gap-3 rounded-lg px-3 py-2" style={{ backgroundColor: "#2c2c2c", border: "1px solid #3a3a3a" }}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm" style={{ color: "#f4f4f4" }}>{note.text}</p>
                    {note.polished && (
                      <p className="text-xs mt-1 italic" style={{ color: "#e93529" }}>→ {note.polished}</p>
                    )}
                    <p className="text-[10px] mt-1" style={{ color: "#8a8a8a" }}>
                      {note.added_by_name || note.added_by || "Unknown"}
                      {note.created_at && ` · ${new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${new Date(note.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await supabase.from("agenda_notes").delete().eq("id", note.id);
                      setAgendaNotes(prev => prev.filter(n => n.id !== note.id));
                    }}
                    className="text-[#8a8a8a] hover:text-[#e93529] transition-colors p-1 flex-shrink-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Add new note */}
          <div className="flex gap-2">
            <input
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onKeyDown={async e => {
                if (e.key === "Enter" && newNote.trim()) {
                  const { data } = await supabase.from("agenda_notes").insert({
                    text: newNote.trim(),
                    added_by: currentEmail,
                    added_by_name: currentName,
                  }).select().single();
                  if (data) setAgendaNotes(prev => [...prev, data]);
                  setNewNote("");
                }
              }}
              placeholder="Type a discussion topic and press Enter..."
              className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pp-red/40"
              style={{ border: "1px solid #3a3a3a", color: "#f4f4f4", backgroundColor: "#2c2c2c" }}
            />
          </div>
        </div>
      </div>

      </div>
      )}

      {/* Quarterly summary modal — triggered by the Summarize Quarter button */}
      <QuarterlySummaryModal
        open={quarterlyModalOpen}
        onClose={() => setQuarterlyModalOpen(false)}
        deals={pipelineStats?.deals || []}
        stageLog={pipelineStats?.stageLog || []}
      />
    </div>
  );
}
